import { createFileRoute, Link } from "@tanstack/react-router";
import { getBlogPosts } from "@/lib/blog.functions";
import type { BlogPostSummary } from "@/lib/blog.server";
import { breadcrumbLd, itemListLd, SITE_OG_IMAGE } from "@/lib/seo";

const TITLE = "Vehicle Guides & Spec Explainers | AutoSpecData Blog";
const DESCRIPTION =
  "Plain-English guides to vehicle specifications: towing capacity, EPA fuel economy ratings, maintenance intervals and how to read manufacturer data correctly.";

export const Route = createFileRoute("/blog/")({
  loader: async () => await getBlogPosts(),
  head: ({ loaderData }) => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://autospecdata.com/blog" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: SITE_OG_IMAGE },
      { name: "twitter:image", content: SITE_OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://autospecdata.com/blog" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          itemListLd(
            "AutoSpecData guides",
            ((loaderData?.posts ?? []) as BlogPostSummary[]).map((p) => ({
              name: p.title,
              path: `/blog/${p.slug}`,
            })),
          ),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog" },
          ]),
        ),
      },
    ],
  }),
  component: BlogIndex,
});

function formatDate(value: string | null) {
  if (!value) return "";
  return new Date(value).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function BlogIndex() {
  const { posts } = Route.useLoaderData();

  return (
    <div>
      <nav className="mx-auto max-w-5xl px-4 pt-4 text-sm text-muted-foreground sm:px-6">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">Blog</span>
      </nav>

      <section className="border-b border-border bg-gradient-to-b from-secondary/40 to-background">
        <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
          <h1 className="text-3xl font-bold text-foreground sm:text-4xl">
            Vehicle guides &amp; spec explainers
          </h1>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-foreground">
            {DESCRIPTION}
          </p>
          <Link to="/best" className="cta-gold mt-6">
            Browse vehicle rankings
          </Link>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        {posts.length === 0 ? (
          <p className="text-muted-foreground">
            No articles published yet — check back shortly.
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(posts as BlogPostSummary[]).map((p) => (
              <Link
                key={p.id}
                to="/blog/$slug"
                params={{ slug: p.slug }}
                className="group flex flex-col rounded-2xl border border-border bg-card p-5 shadow-soft transition-colors hover:border-primary"
              >
                <span className="eyebrow text-primary">{p.category}</span>
                <span className="mt-2 font-heading text-lg font-semibold text-foreground transition-colors group-hover:text-primary">
                  {p.title}
                </span>
                {p.excerpt ? (
                  <span className="mt-2 block text-sm leading-relaxed text-muted-foreground">
                    {p.excerpt}
                  </span>
                ) : null}
                <span className="mt-4 text-xs text-muted-foreground">
                  {formatDate(p.published_at)}
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="mx-auto max-w-3xl px-4 pb-14 sm:px-6">
        <h2 className="font-heading text-xl font-semibold text-foreground">
          What these guides cover
        </h2>
        <div className="mt-4 space-y-4 text-sm leading-relaxed text-muted-foreground">
          <p>
            Manufacturer spec sheets are written to be defensible, not useful. A tow
            rating assumes one cab, bed, axle ratio and a 150-pound driver with no
            passengers. An EPA MPG figure comes from a dynamometer cycle averaging 48 mph
            on the highway loop. A "lifetime" transmission fluid is lifetime only for the
            length of the warranty. These guides translate published figures into the
            number that applies to your vehicle, your configuration and your driving.
          </p>
          <p>
            <span className="font-medium text-foreground">Towing and payload.</span> Max
            tow is the headline; payload is the constraint that actually stops you.
            Tongue weight runs 10–15% of trailer weight and comes straight out of the
            payload figure on the door-jamb sticker, along with every passenger and
            everything in the bed. Most half-ton pickups run out of payload thousands of
            pounds before they run out of tow rating.
          </p>
          <p>
            <span className="font-medium text-foreground">Fuel economy.</span> Combined
            MPG is a fixed 55/45 weighting of two lab cycles, not a prediction. Expect
            10–15% below sticker in normal use and 25% below in cold, short-trip driving.
            Compare gallons per 100 miles rather than MPG when you are working out what a
            difference is worth in money.
          </p>
          <p>
            <span className="font-medium text-foreground">Maintenance.</span> Four items
            carry genuine failure risk — engine oil, timing belt, brake fluid and coolant.
            Most other line items on a dealer schedule are inspections and margin.
            Fluids are age-limited before they are mileage-limited, so a low-mileage car
            is not on a lighter schedule.
          </p>
          <p>
            <span className="font-medium text-foreground">Size and dimensions.</span>
            Published width excludes mirrors; add 8–12 inches. A standard garage bay is
            192 inches deep, which is why a midsize sedan fits everywhere and a full-size
            three-row SUV often does not. Wheelbase predicts rear legroom better than
            overall length does.
          </p>
          <p>
            Every figure quoted across these articles traces back to NHTSA and EPA data
            sets and manufacturer service documentation. Where a number is an estimate or
            a class-typical range rather than a published spec, it says so.
          </p>
        </div>
      </section>
    </div>
  );
}
